import * as THREE from 'three';

/**
 * Triangulate a 3D polygon ring (any orientation) into index triples.
 *
 * A fan from vertex 0 only works for convex rings: on a concave outline the
 * fan triangles cross the concave notches and spill outside the polygon,
 * drawing a sawtooth fill (same bug StockpileVisualization hit). Instead we
 * project the ring onto the plane perpendicular to its Newell normal and let
 * earcut (via ShapeUtils) triangulate it, which is concave-safe. Indices
 * refer to the input ring, so callers keep the real 3D vertex positions.
 */
export const triangulatePolygon = (points: THREE.Vector3[]): number[][] => {
  if (points.length < 3) return [];

  // Newell normal, relative to the first vertex to stay precise at UTM scale.
  const o = points[0];
  const normal = new THREE.Vector3();
  for (let i = 0; i < points.length; i++) {
    const a = points[i];
    const b = points[(i + 1) % points.length];
    const ay = a.y - o.y, az = a.z - o.z, ax = a.x - o.x;
    const by = b.y - o.y, bz = b.z - o.z, bx = b.x - o.x;
    normal.x += (ay - by) * (az + bz);
    normal.y += (az - bz) * (ax + bx);
    normal.z += (ax - bx) * (ay + by);
  }
  if (normal.lengthSq() === 0) return [];
  normal.normalize();

  // Orthonormal basis (u, v) spanning the polygon's plane.
  const helper = Math.abs(normal.z) < 0.9 ? new THREE.Vector3(0, 0, 1) : new THREE.Vector3(1, 0, 0);
  const u = new THREE.Vector3().crossVectors(helper, normal).normalize();
  const v = new THREE.Vector3().crossVectors(normal, u);

  const d = new THREE.Vector3();
  const contour = points.map((p) => {
    d.subVectors(p, o);
    return new THREE.Vector2(d.dot(u), d.dot(v));
  });

  return THREE.ShapeUtils.triangulateShape(contour, []);
};

/**
 * Build a fill mesh geometry for a polygon ring. Positions are relative to
 * `origin` (place the mesh at `origin`) to avoid Float32 precision loss.
 */
export const buildPolygonFillGeometry = (
  points: THREE.Vector3[],
  origin: THREE.Vector3,
): THREE.BufferGeometry | null => {
  const triangles = triangulatePolygon(points);
  if (triangles.length === 0) return null;

  const positions = new Float32Array(triangles.length * 9);
  let k = 0;
  for (const tri of triangles) {
    for (const idx of tri) {
      positions[k++] = points[idx].x - origin.x;
      positions[k++] = points[idx].y - origin.y;
      positions[k++] = points[idx].z - origin.z;
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.computeVertexNormals();
  return geometry;
};
